import { paidPlans } from "@/lib/content/pricing";

export const comparisonCompanyPlan = paidPlans.find(
  (plan) => plan.planCode === "multi_5",
)!;
export const comparisonTeamPlan = paidPlans.find(
  (plan) => plan.planCode === "solo",
)!;

export const comparisonTeamSize = {
  min: 10,
  max: comparisonCompanyPlan.maxTeamMembers,
  initial: 30,
} as const;

// EUR reference rates, 4 September 2026. Keep full precision until display.
export const comparisonUsdToGbp = 0.859 / 1.1622;
export const comparisonExchangeSource =
  "https://www.banque-france.fr/en/statistics/rates-and-prices/exchange-rates-daily-parities-2026-09-04";
export const comparisonExchangeNote =
  "USD estimates use the 4 September 2026 reference rate (£0.7391 per $1), rounded only for display. Taxes and optional add-ons excluded.";

export function formatGbp(value: number, fractionDigits = 0) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export const companyAnnualPrice = comparisonCompanyPlan.yearlyPrice * 12;
export const companyAnnualAmount = formatGbp(companyAnnualPrice);
export const companyMonthlyAmount = formatGbp(comparisonCompanyPlan.monthlyPrice);
export const teamAnnualPrice = comparisonTeamPlan.yearlyPrice * 12;
export const teamAnnualAmount = formatGbp(teamAnnualPrice);
export const teamMonthlyAmount = formatGbp(comparisonTeamPlan.monthlyPrice);
export const comparisonTeamPricingSummary =
  `Up to 10 people: ${teamAnnualAmount}/year (${formatGbp(comparisonTeamPlan.yearlyPrice)}/month billed yearly). 11–50 people: ${companyAnnualAmount}/year (${formatGbp(comparisonCompanyPlan.yearlyPrice)}/month billed yearly). MCP and approved integrations are included in both team-size plans.`;
export const companyAnnualDetails =
  `${formatGbp(comparisonCompanyPlan.yearlyPrice)}/month billed yearly. ${comparisonCompanyPlan.teamLimitLabel}, ${comparisonCompanyPlan.maxTeamMembers} people. Pulse, Fundamentals, Coop Profiles, Roadmap, MCP and approved integrations included. Monthly billing: ${companyMonthlyAmount}.`;

export function getComparisonCooperlyPrice(people: number) {
  const plan = people <= comparisonTeamPlan.maxTeamMembers
    ? comparisonTeamPlan
    : comparisonCompanyPlan;
  const annualPrice = plan.yearlyPrice * 12;
  return {
    plan,
    annualPrice,
    annualAmount: formatGbp(annualPrice),
    monthlyEquivalent: formatGbp(annualPrice / 12 / people, 2),
  };
}

export type ComparisonPriceModel =
  | {
      kind: "per-seat";
      billing: "annual" | "monthly" | "one-time";
      /** Published free plan that covers the whole team up to this size. */
      freeThroughPeople?: number;
      /** Seats that stay free at any team size; only the people above are billed. */
      freeSeats?: number;
      plans: readonly {
        id: string;
        label: string;
        usdPerPerson: number;
        annualMinimumUsd?: number;
        baseOnlyNote?: string;
      }[];
      defaultPlanId: string;
      maxPublishedPeople?: number;
    }
  | {
      kind: "minimum";
      planLabel: string;
      annualUsd: number;
      /** Replaces the quote wording when the floor is the smallest self-serve plan. */
      basis?: string;
    }
  | {
      kind: "quote";
      planLabel: string;
    };

export function calculateTeamPrice(
  people: number,
  model: ComparisonPriceModel,
  planId?: string,
) {
  if (!Number.isSafeInteger(people) || people < 1) {
    throw new RangeError("Team size must be a positive whole number.");
  }

  if (model.kind === "quote") {
    return {
      amountGbp: null,
      label: "Custom quote",
      cadence: "annual billing",
      basis: "Price on request",
      planLabel: model.planLabel,
    };
  }

  if (model.kind === "minimum") {
    const amountGbp = model.annualUsd * comparisonUsdToGbp;
    return {
      amountGbp,
      label: `from ${formatGbp(amountGbp)}`,
      cadence: "/ year",
      basis: model.basis ?? "Published minimum · your quote may be higher",
      planLabel: model.planLabel,
    };
  }

  const plan = model.plans.find(
    (item) => item.id === (planId ?? model.defaultPlanId),
  );
  if (!plan) throw new Error("Unknown comparison pricing plan.");

  if (model.maxPublishedPeople !== undefined && people > model.maxPublishedPeople) {
    return {
      amountGbp: null,
      label: "Request quote",
      cadence: "Enterprise pricing",
      basis: `Published ${plan.label} rate covers up to ${model.maxPublishedPeople} people`,
      planLabel: "Enterprise",
    };
  }

  if (model.freeThroughPeople !== undefined && people <= model.freeThroughPeople) {
    return {
      amountGbp: 0,
      label: formatGbp(0),
      cadence: "free plan",
      basis: `Free plan covers up to ${model.freeThroughPeople} people, with its published limits`,
      planLabel: "Free",
    };
  }

  const multiplier = model.billing === "one-time" ? 1 : 12;
  const billedPeople = Math.max(0, people - (model.freeSeats ?? 0));
  const usd = Math.max(
    billedPeople * plan.usdPerPerson * multiplier,
    plan.annualMinimumUsd ?? 0,
  );
  const amountGbp = usd * comparisonUsdToGbp;
  const cadence = {
    annual: "/ year",
    monthly: "/ year · billed monthly",
    "one-time": "one-time",
  }[model.billing];
  const basis = plan.annualMinimumUsd && usd === plan.annualMinimumUsd
    ? `$${plan.annualMinimumUsd.toLocaleString("en-US")} annual minimum applies`
    : model.freeSeats
      ? `Calculated for ${people} people; the first ${model.freeSeats} are free`
      : `Calculated for ${people} people`;
  return {
    amountGbp,
    label: `${plan.baseOnlyNote ? "from " : ""}≈${formatGbp(amountGbp)}`,
    cadence: `${cadence}${plan.baseOnlyNote ? " · base only" : ""}`,
    basis: plan.baseOnlyNote ? `${basis}. ${plan.baseOnlyNote}` : basis,
    planLabel: plan.label,
  };
}

export function buildComparisonPriceContent(model: ComparisonPriceModel) {
  const alternative = calculateTeamPrice(comparisonTeamSize.initial, model);
  return {
    cooperlyAmount: companyAnnualAmount,
    cooperlyCadence: "/ year",
    cooperlyBasis: "Company plan · annual billing",
    cooperlyDetails: companyAnnualDetails,
    alternativeAmount: alternative.label,
    alternativeCadence: `${alternative.cadence} · ${alternative.planLabel}`,
    alternativeBasis: alternative.basis,
    exchangeNote: model.kind === "quote" ? undefined : comparisonExchangeNote,
    exchangeSourceHref: model.kind === "quote" ? undefined : comparisonExchangeSource,
  };
}
